import React, { useState } from "react";
import { Table, Tooltip } from "antd";
import { DownOutlined, ReloadOutlined, UpOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { getActiveBookings } from "../../actions/reports.action";
import { loadingAction } from "../../actions/loading-actions";
import { isValidNumber, isValid, isValidArray } from "../../utils/utilities";
import { getLocalDateAndTime } from "../../utils/date.utils";
import './dashboard.style.scss';

const SOURCE_COLOR_MAP = {
    redbus: '#E50027',
    website: '#16A34A',
    abhibus: '#2563EB',
    paytm: '#1E40AF',
    counter: '#6B7280',
    makemytrip: '#E11D74',
    goibibo: '#F97316'
};

const renderSourcePill = (text) => {
    if (!isValid(text)) return null;
    const key = String(text).toLowerCase().replace(/\s+/g, '');
    const color = SOURCE_COLOR_MAP[key] || '#6B7280';
    return (
        <span className="active-bookings-source-pill">
            <span className="active-bookings-source-dot" style={{ backgroundColor: color }} />
            <span className="active-bookings-source-name">{text}</span>
        </span>
    );
};

const renderSeatPills = (text) => {
    if (!isValid(text)) return null;
    const seats = String(text).split(',').map((s) => s.trim()).filter(Boolean);
    if (!seats.length) return null;
    return (
        <span className="active-bookings-seats">
            {seats.map((s, i) => (
                <span key={`${s}-${i}`} className="active-bookings-seat-chip">{s}</span>
            ))}
        </span>
    );
};

const renderAmount = (value) => (isValidNumber(value) ? `₹${value.toFixed(2)}` : (isValid(value) ? value : ''));

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDOJ = (dateStr) => {
    if (!isValid(dateStr)) return '';
    const datePart = String(dateStr).split('T')[0];
    const [y, m, d] = datePart.split('-');
    if (!y || !m || !d) return datePart;
    const monthIdx = parseInt(m, 10) - 1;
    return `${d} ${MONTH_SHORT[monthIdx] || m} ${y}`;
};

const columns = [
    { title: 'Source', dataIndex: 'source', key: 'source', render: renderSourcePill },
    { title: 'Total Seats', dataIndex: 'totalSeats', key: 'totalSeats' },
    { title: 'Today', dataIndex: 'today', key: 'today' },
    { title: 'Tomorrow', dataIndex: 'tomorrow', key: 'tomorrow' },
    { title: 'Future', dataIndex: 'future', key: 'future' },
    { title: 'Amount', dataIndex: 'totalAmount', key: 'totalAmount', render: renderAmount }
];

const getShortTripDescription = (description) => {
    if (!isValid(description)) return '';
    const match = description.match(/^(.*?\d{1,2}:\d{2}\s+(?:AM|PM))\b/i);
    return match ? match[1] : description;
};

const groupBlockedDetailsByTicket = (rows) => {
    if (!isValidArray(rows)) return [];
    const grouped = new Map();
    rows.forEach((row, index) => {
        const key = isValid(row.pnr) ? row.pnr : `__row_${index}`;
        if (grouped.has(key)) {
            const existing = grouped.get(key);
            if (isValid(row.seat_number)) existing.seat_numbers.push(row.seat_number);
            if (isValidNumber(row.amount_paid)) existing.amount_paid += row.amount_paid;
        } else {
            grouped.set(key, {
                ...row,
                seat_numbers: isValid(row.seat_number) ? [row.seat_number] : [],
                amount_paid: isValidNumber(row.amount_paid) ? row.amount_paid : 0
            });
        }
    });
    return Array.from(grouped.values()).map((row) => ({
        ...row,
        seat_number: row.seat_numbers.join(', ')
    }));
};

const blockedDetailsColumns = [
    { title: 'SN', key: 'index', render: (text, record, index) => index + 1 },
    { title: 'Agent', dataIndex: 'name', key: 'name' },
    {
        title: 'DOJ',
        dataIndex: 'start_date',
        key: 'start_date',
        render: (text, record) => formatDOJ(record.start_date)
    },
    { title: 'Seat #', dataIndex: 'seat_number', key: 'seat_number', render: renderSeatPills },
    {
        title: 'Trip description',
        dataIndex: 'description',
        key: 'description',
        render: (text) => getShortTripDescription(text)
    }
];

const renderBlockedDetailsExpanded = (record) => (
    <div className="active-bookings-expanded-details">
        <div><span className="active-bookings-expanded-label">Trip description:</span> {isValid(record.description) ? record.description : '-'}</div>
        <div><span className="active-bookings-expanded-label">PNR:</span> {isValid(record.pnr) ? record.pnr : '-'}</div>
        <div><span className="active-bookings-expanded-label">Email:</span> {isValid(record.email_id) ? record.email_id : '-'}</div>
        <div>
            <span className="active-bookings-expanded-label">Booking time:</span>{' '}
            {isValid(record.booking_time) ? getLocalDateAndTime(record.booking_time) : '-'}
        </div>
        <div><span className="active-bookings-expanded-label">Phone No:</span> {isValid(record.phone_number) ? record.phone_number : '-'}</div>
        <div><span className="active-bookings-expanded-label">Amount:</span> {isValidNumber(record.amount_paid) ? `₹${record.amount_paid}` : (isValid(record.amount_paid) ? record.amount_paid : '-')}</div>
    </div>
);

const renderSummary = (pageData) => {
    let totalSeats = 0, today = 0, tomorrow = 0, future = 0, totalAmount = 0;
    pageData.forEach((row) => {
        if (isValidNumber(row.totalSeats)) totalSeats += row.totalSeats;
        if (isValidNumber(row.today)) today += row.today;
        if (isValidNumber(row.tomorrow)) tomorrow += row.tomorrow;
        if (isValidNumber(row.future)) future += row.future;
        if (isValidNumber(row.totalAmount)) totalAmount += row.totalAmount;
    });
    return (
        <Table.Summary fixed>
            <Table.Summary.Row className="active-bookings-summary-row">
                <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                <Table.Summary.Cell index={1}>{totalSeats}</Table.Summary.Cell>
                <Table.Summary.Cell index={2}>{today}</Table.Summary.Cell>
                <Table.Summary.Cell index={3}>{tomorrow}</Table.Summary.Cell>
                <Table.Summary.Cell index={4}>{future}</Table.Summary.Cell>
                <Table.Summary.Cell index={5}>{renderAmount(totalAmount)}</Table.Summary.Cell>
            </Table.Summary.Row>
        </Table.Summary>
    );
};

export const ActiveBookingsComponent = (props) => {

    const {

    } = props;

    const dispatch = useDispatch();
    const [confirmedTickets, setConfirmedTickets] = useState([]);
    const [blockedTickets, setBlockedTickets] = useState([]);
    const [blockedTicketsDetails, setBlockedTicketsDetails] = useState([]);
    const [clickedActiveBookings, setClickedActiveBookings] = useState(false);

    const setLoading = (bool) => {
        dispatch(loadingAction(bool));
    };

    const onSuccess = (data) => {
        setConfirmedTickets(data.confirmedTickets || []);
        setBlockedTickets(data.blockedTickets || []);
        setBlockedTicketsDetails(isValidArray(data.blockedTicketsDetails) ? data.blockedTicketsDetails : []);
        setLoading(false);
    };

    const onFailure = () => {
        setLoading(false);
    };

    const reload = () => {
        setLoading(true);
        getActiveBookings(onSuccess, onFailure);
    };

    const onCardClick = () => {
        if (clickedActiveBookings) {
            setClickedActiveBookings(false);
            return;
        }
        setClickedActiveBookings(true);
        reload();
    };

    const onRefreshClick = (event) => {
        event.stopPropagation();
        if (!clickedActiveBookings) {
            setClickedActiveBookings(true);
        }
        reload();
    };

    const renderSection = (title, dataSource, sectionColumns, withSummary, expandable) => (
        <div className="active-bookings-section">
            <div className="active-bookings-section-title">{title}</div>
            <Table
                className="active-bookings-table"
                size="small"
                rowKey={(record, index) => record.pnr ? `${record.pnr}-${record.seat_number}-${index}` : index}
                dataSource={dataSource}
                columns={sectionColumns}
                pagination={false}
                summary={withSummary ? renderSummary : undefined}
                scroll={{ x: 'max-content' }}
                expandable={expandable}
            />
        </div>
    );

    return (
        <div className="daily-trip-summary active-bookings-card">
            <div
                className="active-bookings-header"
                onClick={onCardClick}
            >
                <span className="active-bookings-title">
                    <span className="active-bookings-status-dot" />
                    <span className="active-bookings-title-text">Active Bookings</span>
                </span>
                <span className="active-bookings-actions">
                    <Tooltip title="Refresh">
                        <ReloadOutlined
                            className="active-bookings-refresh"
                            onClick={onRefreshClick}
                        />
                    </Tooltip>
                    <span className="active-bookings-toggle">
                        {clickedActiveBookings ? <UpOutlined /> : <DownOutlined />}
                    </span>
                </span>
            </div>
            {clickedActiveBookings &&
                <div className="daily-trip-summary-extention active-bookings-extention">
                    {renderSection('Currently Blocked', blockedTickets, columns, true)}
                    {renderSection(
                        'Currently Blocked Seats - Details',
                        groupBlockedDetailsByTicket(blockedTicketsDetails),
                        blockedDetailsColumns,
                        false,
                        {
                            expandedRowRender: renderBlockedDetailsExpanded,
                            expandRowByClick: true
                        }
                    )}
                    {renderSection('Recent Bookings (last 1 hour)', confirmedTickets, columns, true)}
                </div>
            }
        </div>
    );
};
